import Image from '@creator/ui/components/Image/Image';
import Text from '@creator/ui/components/Text/Text';
import { convertFirebaseTimestampToDate, formatDate } from '@creator/ui/utils/date-utils';
import { cn } from '@creator/ui/utils/ui';
import Icon from '@src/basic-components/Icon/Icon';
import UserDisplayNameWithLink from '@src/basic-components/UserDisplayName/UserDisplayNameWithLink';
import { ensureTokenName, ensureTokenUrl } from '@src/model/config/helpers';
import { getToken, getTokenIcon } from '@src/model/token/helpers';
import { ensurePostUrl, isPostDeleted } from '@src/model/upvote/helpers';
import { canViewPendingContent } from '@src/model/upvote/permission';
import { PostStatus } from '@src/store/contentEntities/model';
import { usePost } from '@src/store/posts/hooks/usePost';
import { usePostImage } from '@src/store/posts/hooks/usePostImage';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { type FC, Fragment, memo, type ReactNode, useMemo } from 'react';
import { Link } from 'react-router';
import PendingPostApproveDelete from '../PendingPostApproveDelete/PendingPostApproveDelete';
import PostMenuPopover from '../PostMenuPopover/PostMenuPopover';
import PostStatsAndReactions from '../PostStatsAndReactions/PostStatsAndReactions';

export interface PostCardCompactProps {
    className?: string;
    tokenName: string;
    postId: string;
    children?: ReactNode;
    urlTo?: 'view' | 'edit';
}

const PostCardCompact: FC<PostCardCompactProps> = props => {
    const { tokenName, postId, className, urlTo = 'view' } = props;

    const post = usePost(postId);
    const isDeleted = isPostDeleted(postId);
    const { url: postImageUrl } = usePostImage({ postId, imageType: 'featured' });

    const url = useMemo(getPostUrl, [postId, tokenName]);

    function getPostUrl() {
        if (urlTo === 'edit') {
            // if (post?.status === PostStatus.DRAFT) return `/bbs/${tokenName}/posts/create/${postId}`;
            return `/bbs/${tokenName}/posts/create/${postId}`;
        }
        return `/${ensureTokenUrl(tokenName)}/${ensurePostUrl(postId)}`;
    }

    function getPostCreatedTime() {
        if (!post) return '';
        const { createdAt } = post;
        const _date = convertFirebaseTimestampToDate(createdAt);
        return formatDate(_date);
    }

    function getPostScheduledTime() {
        if (!post) return '';
        const { scheduledAt } = post;
        if (!scheduledAt) return '';
        const _date = convertFirebaseTimestampToDate(scheduledAt);
        return formatDate(_date);
    }

    const renderPostCardCompactFooter = () => {
        if (!post) return null;

        return <PostStatsAndReactions className="h-5 space-x-2 text-gray-500" tokenName={tokenName} isDisabled={isPostDeleted(postId)} postId={postId} />;
    };

    function renderTime() {
        if (!post) return null;
        const { status } = post;
        if (status === PostStatus.PUBLISHED_SCHEDULED) {
            return (
                <div className="flex items-center space-x-1">
                    <Icon name="scheduleClock" className="text-base" />
                    <Text className="">{getPostScheduledTime()}</Text>
                </div>
            );
        }

        if (status === PostStatus.DRAFT) {
            return (
                <div className="flex items-center space-x-1">
                    <Icon name="post" className="text-base" />
                    <Text className="">{getPostCreatedTime()}</Text>
                </div>
            );
        }

        return <Text className="">{getPostCreatedTime()}</Text>;
    }

    function renderByline() {
        if (!post) return null;
        const { publisherName, publisherProfileName, publisherSlugUrl } = post;

        return (
            <div className="flex items-center space-x-2 text-xs">
                <UserDisplayNameWithLink slugUrl={publisherSlugUrl} tokenName={tokenName} profileName={publisherProfileName} displayName={publisherName} />

                <div className="w-[1px] bg-gray-950 dark:bg-gray-50 h-3 shrink-0" />
                {renderTime()}
            </div>
        );
    }

    function renderPostTitle() {
        return (
            <Text enableMultiline={true} as="h3" className="h-10 font-bold line-clamp-2">
                {post?.title}
            </Text>
        );
    }

    function renderMainImage() {
        const token = getToken(tokenName); // deprecated we should use bunny only
        const fallbackImage = token?.imageURI || getTokenIcon(tokenName);

        if (!postImageUrl) return <Image fit="default" src={token?.imageURI} className="object-cover aspect-1/1" />;
        return <Image fit="default" className="object-cover aspect-1/1" src={postImageUrl} fallbackSrc={fallbackImage} />;
    }

    function renderPendingApproveOrDelete() {
        if (post?.status !== PostStatus.PENDING) return null;
        if (!canViewPendingContent(tokenName)) return null;

        return <PendingPostApproveDelete className="flex-col items-center justify-center space-x-0 space-y-4" postId={postId} />;
    }

    return (
        <Link to={url} data-testid="PostCardCompact" data-post-id={postId} className={cn('relative h-28 flex space-x-3 cursor-pointer', isDeleted ? 'opacity-40' : '', className)}>
            {renderMainImage()}
            <div className="flex flex-col flex-1 space-y-2">
                <div className="flex items-start justify-between flex-1 pt-1">
                    {renderPostTitle()}
                    <PostMenuPopover postId={postId} tokenName={tokenName} />
                </div>
                {renderByline()}
                {renderPostCardCompactFooter()}
            </div>
            {renderPendingApproveOrDelete()}
        </Link>
    );
};

export default memo(PostCardCompact, propsEqualityFn);
